import { CIListFilter, DatePicker, getLocaleDateFormat, MultiSelectDropdown, toLocalDate } from 'ccs-ui-components';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import { ExceptionListFilterProps, StartingFilterData } from '../TypeDefs';

/**
 * Compares two arrays of strings and checks if they are equal.
 * @param {string[]} a - The first array.
 * @param {string[]} b - The second array.
 * @returns {boolean} - Returns true if the arrays are equal, false otherwise.
 */
const compareArrays = (a: string[], b: string[]) =>
  a.length === b.length
  && a.every((element, index) => element === b[index]);

const ExceptionListFilter: React.FC<ExceptionListFilterProps> = ({ onApply, data = undefined, allStatusList, onResetFilters }: ExceptionListFilterProps) => {
  const { t: translate } = useTranslation();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [status, setStatus] = useState<string[]>();
  const [allStatusSelected, setAllStatusSelected] = useState(false);
  const [startingValues, setStartingValues] = useState<StartingFilterData>();
  const [applyEnabled, setApplyEnabled] = useState(false);
  const statusValues = useMemo(() => allStatusList.map((s) => s.value), [allStatusList]);

  /**
   * @method applyClickHandler
   * @description handles the apply button for filtering
   */
  const applyClickHandler = () => {
    onApply({
      startDate: startDate ? toLocalDate({ date: startDate, dateTimeFormat: { date: true }, targetDateFormat: 'YYYY-MM-DD' }) : undefined,
      endDate: endDate ? toLocalDate({ date: endDate, dateTimeFormat: { date: true }, targetDateFormat: 'YYYY-MM-DD' }) : undefined,
      status: status ?? [],
    });
  };

  /**
   * @method selectAllHandler
   * @description handles select all status in filters
   * @param selected
   */
  const selectAllHandler = (selected: boolean) => {
    setStatus(selected ? statusValues : []);
    setAllStatusSelected(selected);
  };

  useEffect(() => {
    const sDate = data?.startDate ? new Date(data.startDate) : undefined;
    const eDate = data?.endDate ? new Date(data.endDate) : undefined;
    const statusList = data?.status?.length ? data.status : statusValues;
    setStartDate(sDate);
    setEndDate(eDate);
    setStatus(statusList);

    const copiedData: StartingFilterData = {
      startDate: sDate,
      endDate: eDate,
      status: statusList,
    };
    setAllStatusSelected(copiedData.status.length === statusValues.length);
    setStartingValues(copiedData);
  }, [data]);

  useEffect(() => {
    setApplyEnabled(
      startingValues?.startDate !== startDate
      || startingValues?.endDate !== endDate
      || !compareArrays(startingValues?.status ?? [], status ?? [])
    );
  }, [startDate, endDate, status, startingValues]);

  return (
    <CIListFilter applyEnabled={applyEnabled} onClear={onResetFilters} onApply={applyClickHandler}>
      <Row>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('exception_list_filter.txt_start_date'))}
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date ?? undefined)}
            maxDate={endDate ?? new Date()}
            placeholderText={String(translate('exception_list_filter.txt_date_range'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterStartDate"
          />
        </Col>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('exception_list_filter.txt_end_date'))}
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date ?? undefined)}
            maxDate={new Date()}
            minDate={startDate}
            placeholderText={String(translate('exception_list_filter.txt_date_range'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterEndDate"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <MultiSelectDropdown
            labelText={String(translate('exception_list_filter.txt_type'))}
            options={allStatusList}
            onSelect={(options) => setStatus(options)}
            handleSelectAll={selectAllHandler}
            selectAll={allStatusSelected}
            selectedOptions={status ?? statusValues}
            height="38px"
            data-testid="ciListStatusSelect"
          />
        </Col>
      </Row>
    </CIListFilter>
  );
};
export default ExceptionListFilter;
